import { createFileRoute, Link } from "@tanstack/react-router";
import { t } from "@/i18n";
import { Button, Card, Badge, Spinner, EmptyState } from "@/components/ui";
import { useAssignments, useClassrooms } from "@/lib/queries";

export const Route = createFileRoute("/app/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const { data: assignments, isLoading } = useAssignments({});
  const { data: classrooms } = useClassrooms();

  const active = (assignments ?? []).filter((a) => a.status !== "archived");
  const ready = active.filter((a) => a.status === "ready");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t("dashboard.title")}</h1>
        <Link to="/app/assignments/new" search={{ type: undefined }}>
          <Button>＋ {t("dashboard.createAssignment")}</Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="p-5">
          <p className="text-3xl font-bold">{isLoading ? "…" : active.length}</p>
          <p className="text-sm text-muted-foreground">{t("dashboard.statActive")}</p>
        </Card>
        <Card className="p-5">
          <p className="text-3xl font-bold">{isLoading ? "…" : ready.length}</p>
          <p className="text-sm text-muted-foreground">{t("dashboard.statReady")}</p>
        </Card>
        <Card className="p-5">
          <p className="text-3xl font-bold">{classrooms?.length ?? 0}</p>
          <p className="text-sm text-muted-foreground">{t("dashboard.statClassrooms")}</p>
        </Card>
      </div>

      {(classrooms ?? []).length === 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          {t("dashboard.connectFirst")}{" "}
          <Link to="/app/classes" search={{ connected: false, error: undefined }} className="underline">
            {t("nav.classes")} →
          </Link>
        </div>
      )}

      {isLoading ? (
        <Spinner label={t("app.loading")} />
      ) : active.length === 0 ? (
        <EmptyState>{t("dashboard.noAssignments")}</EmptyState>
      ) : (
        <ul className="space-y-3">
          {active.slice(0, 8).map((a) => (
            <li key={a.id}>
              <Link
                to="/app/assignments/$id"
                params={{ id: a.id }}
                className="flex items-center gap-3 rounded-lg border bg-card p-4 hover:border-primary"
              >
                <span className="font-medium">{a.title}</span>
                <Badge tone="blue">{a.typeName}</Badge>
                <Badge>{a.languageCode.toUpperCase()}</Badge>
                <Badge tone={a.status === "ready" ? "green" : "amber"}>
                  {t(`assignments.status.${a.status}`)}
                </Badge>
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(a.createdAt).toLocaleDateString()}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
